import React from 'react';
import ContainerBox from 'components/ContainerBox';
import { api } from 'common/frontend/api';

import { Prompt } from 'react-router';
import { useForm } from 'react-hook-form';
import { LocationWithContact } from 'common/types';

import { useDispatch, useSelector } from 'react-redux';
import * as UserSelectors from 'selectors/UserSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';

import StoreInformation from './StoreInformation';

import { useTranslation } from 'services/localization';
import SaveButton from 'common/components/SaveButton';
import SetPageTitle from 'components/Portals/SetPageTitle';
import { setOnboardingActionDone } from 'services/onboarding/utils';

const GeneralTab = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId);
	const mainLocationId = useSelector(ShopSelectors.activeShopMainLocationId);
	const locations = useSelector(ShopSelectors.activeShopAllLocations);
	const otherLocations = locations.filter((l) => l.id !== mainLocationId);
	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const activeShopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);

	const activeLocation = locations.find((l) => l.id === activeLocationId);

	const form = useForm<LocationWithContact>({
		defaultValues: activeLocation,
	});

	const { isDirty, isSubmitting, dirtyFields } = form.formState;

	const updateOnboardingActions = () => {
		if (!!dirtyFields.address || !!dirtyFields.phone) {
			setOnboardingActionDone('STORE_DETAILS', activeShopAnalytics);
		}
	};

	const handleSubmit = async (data: LocationWithContact) => {
		const isMainLocation = mainLocationId === activeLocationId;

		try {
			if (isMainLocation) {
				await api()
					.shops.doc(shopId)
					.public.update({
						visitingAddress: {
							...activeLocation,
							...data,
						},
					});
			} else {
				const updatedLocations = otherLocations.map((location) => {
					if (location.id === activeLocationId) {
						return {
							...location,
							...data,
						};
					}

					return location;
				});

				await api().shops.doc(shopId).public.update({
					shopLocations: updatedLocations,
				});
			}
			updateOnboardingActions();
			form.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	return (
		<>
			<SetPageTitle
				title={t('store.storeSettings', 'Store settings')}
				priority={2}
				rightContent={
					<SaveButton
						disabled={!isDirty}
						loading={isSubmitting}
						onClick={form.handleSubmit(handleSubmit)}
						t={t}
					/>
				}
			/>
			<Prompt when={isDirty} message={() => t('common:prompts.unsavedChanges')} />

			<ContainerBox>
				<StoreInformation formControl={form.control} defaultLanguage={defaultLanguage} />
			</ContainerBox>
		</>
	);
};

export default GeneralTab;
